import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";
import type { StaffItem } from "services/api/company/staff.service";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import {
  AvatarCircle,
  AvatarImage,
  DataCell,
  DataLabel,
  DataValue,
  InactiveTag,
  PersonCell,
  PersonName,
  PersonNumber,
  PersonText,
  RowAction,
  StaffRow,
} from "../staff.styled";

interface StaffListRowProps {
  item: StaffItem;
  onSelectWorker: (workerId: number) => void;
}

const toDisplayName = (item: StaffItem): string => {
  const resolvedName = `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim();
  return resolvedName.length > 0 ? resolvedName : String(item.workerId);
};

/**
 * Handles staff list row.
 */
export const StaffListRow = (props: StaffListRowProps): JSX.Element => {
  const { t } = useTranslation();
  const handleSelectWorker = (): void => {
    props.onSelectWorker(props.item.workerId);
  };
  const avatarUrl = useWorkerAvatar({
    workerExternalId: props.item.externalId,
    isWorker: true,
  });
  const displayName = toDisplayName(props.item);
  const employmentTypeLabel = props.item.employmentType
    ? t(`Staff.employmentType${props.item.employmentType}`)
    : "-";

  return (
    <StaffRow
      type="button"
      onClick={handleSelectWorker}
      aria-label={t("Staff.ProfilePanelOpenLabel", {
        worker: displayName,
      })}
    >
      <PersonCell>
        <AvatarCircle>
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={displayName} />
          ) : (
            <CustomIcon name="v2/person" size="20px" />
          )}
        </AvatarCircle>
        <PersonText>
          <PersonName>{displayName}</PersonName>
          <PersonNumber>
            {(props.item.personalIdentityNumber ?? "").length === 12
              ? `${(props.item.personalIdentityNumber ?? "").slice(0, 8)}-${(props.item.personalIdentityNumber ?? "").slice(8, 12)}`
              : "-"}
          </PersonNumber>
        </PersonText>
      </PersonCell>
      <DataCell>
        <DataValue>{employmentTypeLabel}</DataValue>
        <DataLabel>{t("Staff.EmploymentType")}</DataLabel>
      </DataCell>
      <DataCell>
        <DataValue>
          {props.item.phoneNumber
            ? props.item.phoneNumber.startsWith("+")
              ? props.item.phoneNumber
              : `+${props.item.phoneNumber}`
            : "-"}
        </DataValue>
        <DataLabel>{t("Staff.PhoneNumber")}</DataLabel>
      </DataCell>
      <RowAction>
        {!props.item.isActive && (
          <InactiveTag>{t("Staff.Inactive")}</InactiveTag>
        )}
        <CustomIcon name="v2/right-chevron" size="18px" />
      </RowAction>
    </StaffRow>
  );
};
